import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Wifi, WifiOff, Play, Square, Download, Gauge, Thermometer,
  Activity, AlertTriangle, CheckCircle, Circle, Zap,
  Timer, TrendingUp, Settings, Usb, Smartphone, Cable
} from 'lucide-react';
import { CSV_HEADERS } from './obd-pids';

// --- Small presentational components ---

export function ConnectionStatusText({ status, mode }) {
  const STATUS_MAP = {
    disconnected: { text: 'Not Connected', color: 'text-white' },
    connecting: { text: 'Connecting...', color: 'text-white' },
    initializing: { text: 'Initializing...', color: 'text-white' },
    connected: { text: `Connected via ${mode === 'wifi' ? 'Thor WiFi' : mode === 'android' ? 'Android Radio' : mode === 'dcan' ? 'BMW K+DCAN (raw D-CAN)' : 'USB Serial'}`, color: 'text-green-400' },
    error: { text: 'Connection Failed', color: 'text-red-400' },
  };
  const { text, color } = STATUS_MAP[status] || STATUS_MAP.disconnected;
  return <p className={`font-medium ${color}`}>{text}</p>;
}

export function GaugeCard({ icon, label, value, unit, max, color, isLive }) {
  const percentage = value ? Math.min((value / max) * 100, 100) : 0;
  const colors = { orange: 'text-orange-500', blue: 'text-blue-500', green: 'text-green-500', purple: 'text-purple-500' };
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-2">
          <div className={colors[color]}>{icon}</div>
          {isLive && <Circle className="w-2 h-2 text-green-500 fill-green-500 animate-pulse" />}
        </div>
        <p className="text-sm text-white">{label}</p>
        <p className="text-2xl font-bold">
          {value !== undefined ? value.toFixed(0) : '--'}
          <span className="text-sm text-white ml-1">{unit}</span>
        </p>
        <Progress value={percentage} className="mt-2 h-1.5" />
      </CardContent>
    </Card>
  );
}

export function DataRow({ label, value, unit }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
      <span className="text-white">{label}</span>
      <span className="font-mono font-medium text-orange-400">
        {value !== undefined ? value : '--'}
        <span className="text-white ml-1">{unit}</span>
      </span>
    </div>
  );
}

// --- Connection Settings Panel ---

export function ConnectionSettingsPanel({ obd }) {
  const {
    connectionMode, setConnectionMode, isConnected, connectionStatus, error, rawResponses,
    thorIP, setThorIP, thorPort, setThorPort,
    radioIP, setRadioIP, radioPort, setRadioPort, radioProtocol, setRadioProtocol,
    baudRate, setBaudRate,
    useDirectBridge, setUseDirectBridge, bridgeUrl, setBridgeUrl,
    handleConnect, disconnect,
  } = obd;

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-orange-500" />Connection Settings
        </CardTitle>
        <CardDescription>Choose your OBD adapter type</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Direct Local Bridge toggle */}
        <div className={`rounded-lg border p-3 ${useDirectBridge ? 'bg-orange-500/10 border-orange-500/40' : 'bg-zinc-800/40 border-zinc-700'}`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="direct-bridge-toggle"
                data-testid="direct-bridge-toggle"
                checked={useDirectBridge}
                onChange={(e) => setUseDirectBridge(e.target.checked)}
                disabled={isConnected}
                className="mt-1 h-4 w-4 accent-orange-500"
              />
              <div>
                <label htmlFor="direct-bridge-toggle" className="text-sm font-semibold text-white cursor-pointer">
                  Direct Local Bridge <span className="text-orange-400">(recommended for iPhone hotspot)</span>
                </label>
                <p className="text-xs text-zinc-400 mt-0.5">
                  Browser talks straight to a tiny Python bridge on your device. Bypasses our cloud — works on any private LAN (172.20.10.x, 192.168.x.x).
                </p>
              </div>
            </div>
            <a
              href="/tools/thor_bridge.py"
              download="thor_bridge.py"
              data-testid="download-bridge-btn"
              className="text-xs font-medium px-3 py-1.5 rounded border border-orange-500/50 text-orange-400 hover:bg-orange-500/10 whitespace-nowrap"
            >
              <Download className="w-3.5 h-3.5 inline mr-1" />Download Bridge
            </a>
          </div>
          {useDirectBridge && (
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs text-zinc-300">Bridge WebSocket URL</label>
                <Input
                  value={bridgeUrl}
                  onChange={(e) => setBridgeUrl(e.target.value)}
                  placeholder="ws://127.0.0.1:8765"
                  className="w-56 bg-zinc-900 border-zinc-700 font-mono text-xs"
                  disabled={isConnected}
                  data-testid="bridge-url-input"
                />
              </div>
              <a
                href="/tools/README_BRIDGE.md"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:underline pb-2"
              >
                Setup guide &rarr;
              </a>
            </div>
          )}
        </div>

        <Tabs value={connectionMode} onValueChange={setConnectionMode}>
          <TabsList className="bg-zinc-800">
            <TabsTrigger value="android" className="data-[state=active]:bg-orange-500/20"><Smartphone className="w-4 h-4 mr-2" />Android Radio</TabsTrigger>
            <TabsTrigger value="wifi" className="data-[state=active]:bg-orange-500/20"><Wifi className="w-4 h-4 mr-2" />Thor WiFi</TabsTrigger>
            <TabsTrigger value="serial" className="data-[state=active]:bg-orange-500/20"><Usb className="w-4 h-4 mr-2" />USB (ELM327)</TabsTrigger>
            <TabsTrigger value="dcan" data-testid="dcan-tab-trigger" className="data-[state=active]:bg-emerald-500/20"><Cable className="w-4 h-4 mr-2" />BMW K+DCAN</TabsTrigger>
          </TabsList>

          <TabsContent value="android" className="mt-4">
            <div className="space-y-4">
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <Smartphone className="w-4 h-4 text-blue-400" />
                <AlertTitle className="text-blue-400">K-DCAN via Android Radio</AlertTitle>
                <AlertDescription className="text-blue-300/80">
                  Connect your K-DCAN cable to the radio's USB port. Apps like ProTool or Torque create a TCP bridge that this portal can connect to over WiFi.
                </AlertDescription>
              </Alert>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="space-y-2">
                  <label className="text-sm text-white">Radio IP Address</label>
                  <Input value={radioIP} onChange={(e) => setRadioIP(e.target.value)} placeholder="192.168.1.xxx" className="w-44 bg-zinc-800 border-zinc-700" disabled={isConnected} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-white">Port</label>
                  <Input value={radioPort} onChange={(e) => setRadioPort(e.target.value)} placeholder="35000" className="w-24 bg-zinc-800 border-zinc-700" disabled={isConnected} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-white">Protocol</label>
                  <Select value={radioProtocol} onValueChange={setRadioProtocol} disabled={isConnected}>
                    <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="elm327">ELM327</SelectItem>
                      <SelectItem value="kwp2000">KWP2000 (BMW)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="text-xs text-white space-y-1">
                <p><strong>How to find your Radio IP:</strong></p>
                <ol className="list-decimal list-inside pl-2 space-y-0.5">
                  <li>Open Settings on your Android radio</li>
                  <li>Go to WiFi &rarr; Your network &rarr; View IP address</li>
                  <li>Or use ProTool's network settings to find the TCP server IP/port</li>
                </ol>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="wifi" className="mt-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <label className="text-sm text-white">Thor IP Address</label>
                <Input value={thorIP} onChange={(e) => setThorIP(e.target.value)} placeholder="192.168.0.10" className="w-40 bg-zinc-800 border-zinc-700" disabled={isConnected} />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-white">Port</label>
                <Input value={thorPort} onChange={(e) => setThorPort(e.target.value)} placeholder="35000" className="w-24 bg-zinc-800 border-zinc-700" disabled={isConnected} />
              </div>
            </div>
            <p className="text-xs text-white mt-2">Connect your device to Thor WiFi network first, then click Connect</p>
          </TabsContent>

          <TabsContent value="serial" className="mt-4">
            <Alert className="bg-amber-500/10 border-amber-500/30 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <AlertTitle className="text-amber-400">ELM327 USB adapters only</AlertTitle>
              <AlertDescription className="text-amber-300/80 text-xs">
                USB Serial speaks the <strong>ELM327 AT-command</strong> protocol. Plain
                <strong> K+DCAN cables bundled with INPA/ISTA do NOT work here</strong> — those
                speak BMW D-CAN/KWP and need a dedicated driver.
                <div className="mt-2 flex flex-wrap gap-3">
                  <a
                    href="/tools/tool32_datalog_guide.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tool32-guide-link"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 font-medium"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Got a K+DCAN? Use it with Tool32 + upload CSV → full guide
                  </a>
                </div>
                <div className="mt-2">
                  Otherwise, a real ELM327 USB adapter (OBDLink SX ≈ $30, Veepeak ≈ $25) plugs in and streams to the live gauges directly.
                </div>
              </AlertDescription>
            </Alert>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <label className="text-sm text-white">Baud Rate</label>
                <Select value={baudRate} onValueChange={setBaudRate} disabled={isConnected}>
                  <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="9600">9600 (genuine ELM)</SelectItem>
                    <SelectItem value="38400">38400 (most clones)</SelectItem>
                    <SelectItem value="115200">115200 (OBDLink)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-white mt-2">
              If no data flows after "Start Logging", try switching baud rate (38400 is most common for cheap ELM clones) and reconnect.
            </p>
          </TabsContent>

          <TabsContent value="dcan" className="mt-4">
            <Alert className="bg-emerald-500/10 border-emerald-500/30 mb-3">
              <Cable className="w-4 h-4 text-emerald-400" />
              <AlertTitle className="text-emerald-400">BMW K+DCAN cable — raw D-CAN (no ELM327 needed)</AlertTitle>
              <AlertDescription className="text-emerald-300/80 text-xs space-y-2">
                <p>
                  This mode talks to your INPA/ISTA K+DCAN cable directly using <strong>BMW-FAST
                  telegrams</strong> over Web Serial — the same framing Tool32/EDIABAS uses. It reads
                  live values via OBD-II Mode 01 wrapped in BMW-FAST, so it works on <strong>2008+
                  D-CAN cars</strong> (E9x, etc.) without SGBD files.
                </p>
                <p className="text-emerald-200/90">
                  <strong>One-time setup for reliable timing:</strong> in Windows Device Manager →
                  Ports (COM &amp; LPT) → your USB Serial Port → Properties → Port Settings →
                  Advanced → set <strong>Latency Timer = 1&nbsp;msec</strong>. The browser cannot set
                  this itself. Chrome or Edge on desktop only.
                </p>
                <ol className="list-decimal list-inside pl-1 space-y-0.5">
                  <li>Plug the K+DCAN cable into the OBD port and a USB port.</li>
                  <li>Turn ignition to position II (engine running for live RPM/load).</li>
                  <li>Click Connect, then pick the cable&apos;s COM port in the browser prompt.</li>
                  <li>Watch the Communication Log — you should see <span className="text-blue-300">TX</span> frames and green <span className="text-green-300">RX</span> frames back.</li>
                </ol>
              </AlertDescription>
            </Alert>
            <p className="text-xs text-white">
              Fixed at 115200 baud, 8N1. Targets the DME (0x12) from tester (0xF1). Read-only —
              no ECU writes are sent.
            </p>
          </TabsContent>
        </Tabs>

        {/* Connect/Disconnect */}
        <div className="flex gap-2 pt-2">
          {!isConnected ? (
            <Button onClick={handleConnect} className="bg-green-500 hover:bg-green-600 text-white" disabled={connectionStatus === 'connecting'}>
              {connectionMode === 'wifi' ? <Wifi className="w-4 h-4 mr-2" /> : <Usb className="w-4 h-4 mr-2" />}
              {connectionStatus === 'connecting' ? 'Connecting...' : 'Connect'}
            </Button>
          ) : (
            <Button onClick={disconnect} variant="outline" className="border-red-500/50 text-red-400">
              <WifiOff className="w-4 h-4 mr-2" />Disconnect
            </Button>
          )}
        </div>

        {/* Status */}
        <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
          {isConnected ? <CheckCircle className="w-5 h-5 text-green-500" /> : connectionStatus === 'error' ? <AlertTriangle className="w-5 h-5 text-red-500" /> : <Circle className="w-5 h-5 text-white" />}
          <div>
            <ConnectionStatusText status={connectionStatus} mode={connectionMode} />
            {error && <p className="text-sm text-red-400">{error}</p>}
          </div>
        </div>

        {/* Communication Log */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-white">Communication Log:</p>
            <button
              type="button"
              data-testid="copy-comm-log-btn"
              onClick={() => {
                const text = rawResponses.map(r => `[${r.time}] ${r.msg}`).join('\n');
                if (text) navigator.clipboard?.writeText(text).catch(() => {});
              }}
              className="text-xs px-2 py-1 rounded border border-zinc-600 text-zinc-300 hover:bg-zinc-800 hover:text-white"
            >
              Copy log
            </button>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 h-32 overflow-y-auto font-mono text-xs">
            {rawResponses.length === 0 ? (
              <p className="text-white">Waiting for connection...</p>
            ) : rawResponses.map((r, i) => {
              let colorClass = 'text-white';
              if (r.msg.startsWith('TX:')) colorClass = 'text-blue-400';
              else if (r.msg.startsWith('RX:')) colorClass = 'text-green-400';
              else if (r.msg.includes('ERROR')) colorClass = 'text-red-400';
              return <div key={`${r.time}-${i}`} className={colorClass}>[{r.time}] {r.msg}</div>;
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Logging Controls Panel ---

export function LoggingControlsPanel({ isConnected, isLogging, logHistory, rawResponses, startLogging, stopLogging }) {
  const [handoff, setHandoff] = React.useState(null);
  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // Build the datalog CSV string from logHistory. Returns '' if no rows.
  const buildDatalogCsv = () => {
    if (logHistory.length === 0) return '';
    const rows = []; let currentRow = {}; let lastTime = 0;
    logHistory.forEach(entry => {
      if (entry.timestamp - lastTime > 300) { if (Object.keys(currentRow).length > 0) rows.push(currentRow); currentRow = { timestamp: entry.timestamp }; }
      currentRow[entry.pid] = entry.value; lastTime = entry.timestamp;
    });
    if (Object.keys(currentRow).length > 0) rows.push(currentRow);

    let csv = CSV_HEADERS.join(',') + '\n';
    rows.forEach(row => {
      csv += [row.timestamp || '', row.RPM?.toFixed(0) || '', row.SPEED?.toFixed(1) || '', row.ENGINE_LOAD?.toFixed(1) || '',
        row.THROTTLE?.toFixed(1) || '', row.AFR?.toFixed(2) || '', row.COOLANT_TEMP?.toFixed(1) || '', row.INTAKE_TEMP?.toFixed(1) || '',
        row.TIMING_ADV?.toFixed(1) || '', row.STFT_B1?.toFixed(2) || '', row.LTFT_B1?.toFixed(2) || '', row.MAF?.toFixed(2) || ''
      ].join(',') + '\n';
    });
    return csv;
  };

  const csvFilename = () => `livelog_${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.csv`;

  const downloadLog = () => {
    const csv = buildDatalogCsv();
    if (!csv) {
      if (rawResponses.length > 0) {
        downloadFile(rawResponses.map(r => `${r.time},${r.msg}`).join('\n'), 'raw_log.txt', 'text/plain');
      } else { alert('No data to download'); }
      return;
    }
    downloadFile(csv, csvFilename(), 'text/csv');
  };

  // Push the recorded session straight into the AI Iterate tab as its datalog.
  const sendToIterate = () => {
    const csv = buildDatalogCsv();
    if (!csv) { alert('Record some data first, then send it to AI Iterate.'); return; }
    const file = new File([csv], csvFilename(), { type: 'text/csv' });
    window.__pendingLiveLog = file; // consumed by AITuneIterator on mount
    window.dispatchEvent(new CustomEvent('live-log-to-iterate', { detail: { file } }));
    setHandoff('Sent to AI Iterate — switch to the AI Iterate tab and drop your current .bin.');
    setTimeout(() => setHandoff(null), 6000);
  };

  if (!isConnected) return null;

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Timer className="w-5 h-5 text-orange-500" />Data Recording</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isLogging ? (
              <Button onClick={startLogging} className="bg-orange-500 hover:bg-orange-600"><Play className="w-4 h-4 mr-2" />Start Logging</Button>
            ) : (
              <Button onClick={stopLogging} variant="destructive"><Square className="w-4 h-4 mr-2" />Stop</Button>
            )}
            {isLogging && (
              <div className="flex items-center gap-2">
                <Circle className="w-3 h-3 text-red-500 animate-pulse fill-red-500" />
                <span className="text-red-400 font-medium">RECORDING</span>
              </div>
            )}
            <Badge variant="outline" className="border-zinc-600">{logHistory.length} samples</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" onClick={sendToIterate} data-testid="send-to-iterate-btn">
              <TrendingUp className="w-4 h-4 mr-2" />Send to AI Iterate
            </Button>
            <Button variant="outline" className="border-orange-500/50 text-orange-400" onClick={downloadLog} data-testid="download-csv-btn">
              <Download className="w-4 h-4 mr-2" />Download CSV
            </Button>
          </div>
        </div>
        {handoff && (
          <p className="mt-3 text-sm text-emerald-400 flex items-center gap-2" data-testid="iterate-handoff-msg">
            <CheckCircle className="w-4 h-4" />{handoff}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// --- Live Gauges & Data Display ---

export function LiveGauges({ liveData, isLogging }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <GaugeCard icon={<Gauge className="w-5 h-5" />} label="RPM" value={liveData.RPM?.value} unit="RPM" max={7200} color="orange" isLive={isLogging} />
      <GaugeCard icon={<Activity className="w-5 h-5" />} label="Speed" value={liveData.SPEED?.value} unit="MPH" max={160} color="blue" isLive={isLogging} />
      <GaugeCard icon={<Zap className="w-5 h-5" />} label="Load" value={liveData.ENGINE_LOAD?.value} unit="%" max={100} color="green" isLive={isLogging} />
      <GaugeCard icon={<TrendingUp className="w-5 h-5" />} label="Throttle" value={liveData.THROTTLE?.value} unit="%" max={100} color="purple" isLive={isLogging} />
    </div>
  );
}

export function DetailedDataPanels({ liveData }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Gauge className="w-5 h-5 text-orange-500" />Engine Data</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <DataRow label="RPM" value={liveData.RPM?.value?.toFixed(0)} unit="RPM" />
          <DataRow label="Load" value={liveData.ENGINE_LOAD?.value?.toFixed(1)} unit="%" />
          <DataRow label="Throttle" value={liveData.THROTTLE?.value?.toFixed(1)} unit="%" />
          <DataRow label="AFR" value={liveData.AFR?.value?.toFixed(2)} unit="AFR" />
          <DataRow label="Timing" value={liveData.TIMING_ADV?.value?.toFixed(1)} unit="°" />
          <DataRow label="MAF" value={liveData.MAF?.value?.toFixed(2)} unit="g/s" />
        </CardContent>
      </Card>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Thermometer className="w-5 h-5 text-orange-500" />Temps & Fuel</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <DataRow label="Coolant" value={liveData.COOLANT_TEMP?.value?.toFixed(1)} unit="°F" />
          <DataRow label="Intake" value={liveData.INTAKE_TEMP?.value?.toFixed(1)} unit="°F" />
          <DataRow label="STFT B1" value={liveData.STFT_B1?.value?.toFixed(2)} unit="%" />
          <DataRow label="LTFT B1" value={liveData.LTFT_B1?.value?.toFixed(2)} unit="%" />
          <DataRow label="Speed" value={liveData.SPEED?.value?.toFixed(1)} unit="MPH" />
        </CardContent>
      </Card>
    </div>
  );
}

// --- BMW Request Explorer (read-only reverse-engineering for knock/VANOS) ---

export function DCANRequestExplorer({ obd }) {
  const { sendRawDCAN, connectionMode, isConnected } = obd;
  const [hex, setHex] = React.useState('21 0C');
  if (connectionMode !== 'dcan' || !isConnected) return null;

  const parseBytes = (s) =>
    s.trim().split(/[\s,]+/).filter(Boolean)
      .map((x) => parseInt(x, 16))
      .filter((b) => Number.isInteger(b) && b >= 0 && b <= 0xff);

  const send = () => {
    const bytes = parseBytes(hex);
    if (bytes.length && sendRawDCAN) sendRawDCAN(bytes);
  };

  const presets = [
    { label: 'Start session', bytes: '10 87' },
    { label: 'Tester present', bytes: '3E' },
    { label: 'RDBLI 21 0C', bytes: '21 0C' },
    { label: 'RDBI 22 F4 05', bytes: '22 F4 05' },
    { label: 'Read RAM 23', bytes: '23' },
  ];

  return (
    <Card className="bg-zinc-900 border-emerald-800/50" data-testid="dcan-explorer">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Settings className="w-5 h-5 text-emerald-500" />BMW Request Explorer
          <Badge variant="outline" className="border-emerald-600 text-emerald-400 ml-1">read-only</Badge>
        </CardTitle>
        <CardDescription>
          Send a raw KWP request to the DME (wrapped in BMW-FAST automatically) to hunt down knock/VANOS
          measurements. Watch the green <span className="text-green-400">RX</span> frames in the Communication Log above.
          No writes are ever sent.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => setHex(p.bytes)}
              className="text-xs px-2.5 py-1 rounded border border-zinc-600 text-zinc-300 hover:bg-zinc-800 hover:text-white font-mono"
              data-testid={`dcan-preset-${p.bytes.replace(/\s+/g, '')}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="space-y-1 flex-1 min-w-[200px]">
            <label className="text-xs text-zinc-300">Request bytes (hex, e.g. &quot;21 0C&quot;)</label>
            <Input
              value={hex}
              onChange={(e) => setHex(e.target.value)}
              placeholder="21 0C"
              className="bg-zinc-950 border-zinc-700 font-mono"
              data-testid="dcan-request-input"
            />
          </div>
          <Button onClick={send} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dcan-send-btn">
            <Zap className="w-4 h-4 mr-2" />Send
          </Button>
        </div>
        <p className="text-[11px] text-zinc-500 leading-relaxed">
          Tip: many MSV80 live values (knock, VANOS) are read via service <span className="font-mono text-zinc-300">0x21</span> (readDataByLocalIdentifier)
          or <span className="font-mono text-zinc-300">0x23</span> (readMemoryByAddress). If a request returns a <span className="font-mono">7F</span> (negative response),
          try Start session (10 87) first. Copy the RX frames back to your tuner and they&apos;ll lock in the decode.
        </p>
      </CardContent>
    </Card>
  );
}
